import { PRODUCT_IDS } from 'constants/product-ids'

// Discount constants organized by product type
const SALES_ENABLEMENT_DISCOUNTS: { [period: string]: number } = {
  quarterly: 0,
  semi_annual: 0,
  yearly: 0,
}

const LINQ_ONE_WITH_BLUE_DISCOUNTS: { [period: string]: number } = {
  quarterly: 0,
  semi_annual: 0,
  yearly: 0,
}

const LINQ_ONE_STANDALONE_DISCOUNTS: { [period: string]: number } = {
  quarterly: 30,
  semi_annual: 15,
  yearly: 30,
}

/**
 * Normalizes period strings to standard format
 * Handles variations like "3 months", "3_months", "quarterly", etc.
 */
const normalizePeriod = (period: string): string => {
  const normalized = period.toLowerCase().replace(/\s+/g, '_')
  const periodMap: { [key: string]: string } = {
    '3_months': 'quarterly',
    quarterly: 'quarterly',
    '6_months': 'semi_annual',
    semi_annual: 'semi_annual',
    year: 'yearly',
    yearly: 'yearly',
    annual: 'yearly',
  }
  return periodMap[normalized] || normalized
}

/**
 * Converts period string to number of months
 */
const getPeriodInMonths = (period: string): number => {
  const normalized = period.toLowerCase().replace(/\s+/g, '_')
  const periodMap: { [key: string]: number } = {
    '3_months': 3,
    quarterly: 3,
    '6_months': 6,
    semi_annual: 6,
    '12_months': 12,
    yearly: 12,
    year: 12,
    annual: 12,
  }
  return periodMap[normalized] || 1
}

/**
 * Business logic for calculating automatic discounts based on product combinations
 * Implements complex discount rules:
 * - Linq Blue (Sales Enablement): No automatic discount
 * - Linq One standalone: 30% quarterly/yearly, 15% semi-annual
 * - Linq One + Linq Blue: No automatic discount
 */
export class DiscountCalculator {
  static getAutomaticDiscount(
    productId: string,
    period: string,
    lineItems: { productId: string; period: string }[]
  ): number {
    const hasSalesEnablement = lineItems.some(
      (item) => item.productId === PRODUCT_IDS.LINQ_BLUE
    )

    const linkedBlueItem = lineItems.find(
      (item) => item.productId === PRODUCT_IDS.LINQ_BLUE
    )
    const normalizedBlueItemPeriod = linkedBlueItem
      ? normalizePeriod(linkedBlueItem.period)
      : ''

    const normalizedPeriod = normalizePeriod(period)

    // Linq Blue (Sales Enablement) discount
    if (productId === PRODUCT_IDS.LINQ_BLUE) {
      return SALES_ENABLEMENT_DISCOUNTS[normalizedPeriod] || 0
    }

    // Linq One discount logic
    if (productId === PRODUCT_IDS.LINQ_ONE) {
      if (
        hasSalesEnablement &&
        normalizedBlueItemPeriod === 'yearly' &&
        normalizedPeriod === 'yearly'
      ) {
        // Both are yearly: use special discount for Linq One
        return LINQ_ONE_WITH_BLUE_DISCOUNTS[normalizedPeriod] || 0
      }
      if (hasSalesEnablement) {
        // Linq Blue exists but not both yearly: use Linq Blue's discounts
        return SALES_ENABLEMENT_DISCOUNTS[normalizedPeriod] || 0
      }
      return LINQ_ONE_STANDALONE_DISCOUNTS[normalizedPeriod] || 0
    }

    return 0
  }

  static getDiscountPriceId(
    productBundle: any,
    period: string,
    discountPercentage: number,
    productId?: string
  ): string | undefined {
    if (!productBundle?.prices || !discountPercentage) return undefined

    const expectedMonths = getPeriodInMonths(period)

    // Get prices for this specific product
    const pricesForProduct = productId ? productBundle.prices[productId] : []

    if (!Array.isArray(pricesForProduct) || pricesForProduct.length === 0) {
      return undefined
    }

    for (let i = 0; i < pricesForProduct.length; i += 1) {
      const price = pricesForProduct[i]

      if (price) {
        const isDiscount =
          price.metadata?.is_discount === true ||
          price.metadata?.is_discount === 'true'
        const discountPercentageMetadata = parseFloat(
          price.metadata?.discount_percentage || '0.00'
        )

        if (isDiscount && discountPercentageMetadata === discountPercentage) {
          // Check if one-time product (recurring is null)
          if (!price.recurring) {
            // For one-time products, match by billing_period_months
            const billingPeriodMonths = price.metadata?.billing_period_months
            if (billingPeriodMonths === expectedMonths?.toString()) {
              return price.id
            }
          } else {
            // For recurring products, match by interval and interval_count
            const { interval_count: intervalCount = 1, interval } =
              price.recurring

            if (
              expectedMonths === 3 &&
              interval === 'month' &&
              intervalCount === 3
            ) {
              return price.id
            }
            if (
              expectedMonths === 6 &&
              interval === 'month' &&
              intervalCount === 6
            ) {
              return price.id
            }
            if (
              expectedMonths === 12 &&
              interval === 'year' &&
              intervalCount === 1
            ) {
              return price.id
            }
          }
        }
      }
    }

    return undefined
  }

  static getPeriodInMonths(period: string): number {
    return getPeriodInMonths(period)
  }

  static normalizePeriod(period: string): string {
    return normalizePeriod(period)
  }

  static getProductIds() {
    return PRODUCT_IDS
  }
}
